import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useState } from 'react';
import { Dropdown, message, Modal, Tooltip } from 'antd';
import { DesktopOutlined, PlayCircleOutlined, PaperClipOutlined, PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import Flex from 'components/shared-components/Flex';
import { SPACER, FONT_SIZES } from 'constants/ThemeConstant';
import MediModel from '../Campaign/MediModel';
import { useMyContext } from 'Context/MyContextProvider';
import apiClient from 'services/apiClient';
import { isValidDeviceFileForKind, MEDIA_KINDS } from './mediaKindUtils';

const Icon = styled.div(() => ({
    fontSize: FONT_SIZES.LG,
}));

const MenuItem = (props) => (
    <Flex as="span" alignItems="center" gap={SPACER[2]}>
        <Icon>{props?.icon}</Icon>
        <span>{props?.label}</span>
    </Flex>
);

const MAX_FILE_SIZE_MB = 15;

export const UploadDrawer = forwardRef(({
    GetMedia,
    disabled,
    setDisable,
    number,
    variant = 'paperclip',
    showLabel,
    selectedCount = 0,
    maxSelection = 10,
    selectedMediaIds = [],
    beginDeviceMediaUploads,
    completeDeviceMediaUpload,
    failDeviceMediaUpload,
    onMenuSelect,
    mediaKind = 'image',
    hideTrigger = false,
    fileInputId: fileInputIdProp,
    openLibraryRequest = null,
    onLibraryOpened,
    parentNumber = '',
}, ref) => {
    const { UserData } = useMyContext();
    const resolvedParentNumber = String(
        parentNumber || UserData?.reporting_user?.whatsapp_number || UserData?.whatsapp_number || ''
    );
    const [openMediaModel, setOpenMediaModel] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const generatedFileInputId = useId();
    const fileInputId = fileInputIdProp || generatedFileInputId;
    const slotsLeft = Math.max(0, maxSelection - selectedCount);
    const kindConfig = MEDIA_KINDS[mediaKind] || MEDIA_KINDS.image;
    const devicePickerDisabled = disabled || slotsLeft <= 0 || isUploading || !mediaKind;

    useEffect(() => {
        if (openLibraryRequest && openLibraryRequest === mediaKind) {
            if (slotsLeft <= 0) {
                message.warning(`Maximum ${maxSelection} files allowed`);
                onLibraryOpened?.();
                return;
            }
            setOpenMediaModel(true);
            onLibraryOpened?.();
        }
    }, [openLibraryRequest, mediaKind, slotsLeft, maxSelection, onLibraryOpened]);

    const validateDeviceFile = useCallback((file, kind) => {
        const config = MEDIA_KINDS[kind] || MEDIA_KINDS.image;
        const name = file.name;
        const fileSizeMB = file.size / (1024 * 1024);

        if (!isValidDeviceFileForKind(file, kind)) {
            message.error(`"${name}" is not a supported ${config.label.toLowerCase()} file.`);
            return false;
        }

        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            message.error(`"${name}" exceeds the maximum size of ${MAX_FILE_SIZE_MB} MB.`);
            return false;
        }

        return true;
    }, []);

    const uploadDeviceFiles = useCallback(async (files) => {
        const pending = beginDeviceMediaUploads?.(files, selectedCount) || [];

        if (!pending.length) {
            return;
        }

        const formData = new FormData();
        pending.forEach(({ file }, index) => {
            formData.append(`files[${index}]`, file);
        });
        formData.append('parent_number', resolvedParentNumber);
        formData.append('child_number', String(number ?? ''));

        setIsUploading(true);
        setDisable(false);

        try {
            const response = await apiClient.post('bulk-store-from-device-media', formData, {
                timeout: 120000,
            });
            const items = response.data?.items || [];
            let successCount = 0;

            pending.forEach(({ clientId }, index) => {
                const result = items.find((item) => item.index === index) || items[index];

                if (result?.success && result.media_id) {
                    completeDeviceMediaUpload?.(
                        clientId,
                        result.media_id,
                        result.name,
                        result.media_url
                    );
                    successCount += 1;
                    return;
                }

                failDeviceMediaUpload?.(clientId);
                message.error(result?.error || `Failed to upload "${pending[index].file.name}"`);
            });

            if (successCount > 0 && successCount < pending.length) {
                message.warning(`${successCount} of ${pending.length} file(s) uploaded`);
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            pending.forEach(({ clientId }) => failDeviceMediaUpload?.(clientId));
            message.error('Failed to upload media files');
        } finally {
            setIsUploading(false);
        }
    }, [
        beginDeviceMediaUploads,
        completeDeviceMediaUpload,
        failDeviceMediaUpload,
        number,
        selectedCount,
        setDisable,
        resolvedParentNumber,
    ]);

    const processDeviceFiles = useCallback(async (files, kind) => {
        const fileList = Array.from(files || []);
        if (!fileList.length || !kind) {
            return;
        }

        if (slotsLeft <= 0) {
            message.warning(`Maximum ${maxSelection} files allowed`);
            return;
        }

        const batch = fileList.slice(0, slotsLeft);
        if (fileList.length > slotsLeft) {
            message.warning(`Only ${slotsLeft} more file(s) can be added`);
        }

        const validFiles = batch.filter((file) => validateDeviceFile(file, kind));
        if (!validFiles.length) {
            return;
        }

        await uploadDeviceFiles(validFiles);
    }, [maxSelection, slotsLeft, uploadDeviceFiles, validateDeviceFile]);

    useImperativeHandle(ref, () => ({
        processDeviceFiles,
    }), [processDeviceFiles]);

    const handleDeviceFileChange = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (!files.length) {
            return;
        }

        await processDeviceFiles(files, mediaKind);
    };

    const HandleUploadImage = (mediaId, name, link) => {
        if (mediaId) {
            GetMedia(mediaId, name, link);
        }
    };

    const menuItems = [
        {
            key: 'from-media',
            label: <MenuItem label="From Media" icon={<PlayCircleOutlined />} />,
            disabled: devicePickerDisabled,
        },
        {
            key: 'from-device',
            label: (
                <div
                    style={{
                        position: 'relative',
                        margin: '-5px -12px',
                        padding: '5px 12px',
                        cursor: devicePickerDisabled ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => {
                        if (slotsLeft <= 0) {
                            message.warning(`Maximum ${maxSelection} files allowed`);
                        }
                    }}
                >
                    <MenuItem label="From Device" icon={<DesktopOutlined />} />
                    {!devicePickerDisabled && (
                        <input
                            id={fileInputId}
                            type="file"
                            multiple
                            accept={kindConfig.accept}
                            onChange={handleDeviceFileChange}
                            onClick={(event) => {
                                event.stopPropagation();
                                setDropdownOpen(false);
                                onMenuSelect?.();
                            }}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                            }}
                        />
                    )}
                </div>
            ),
        },
    ];

    const onMenuClick = ({ key }) => {
        if (key === 'from-media') {
            if (slotsLeft <= 0) {
                message.warning(`Maximum ${maxSelection} files allowed`);
                return;
            }
            setOpenMediaModel(true);
            setDropdownOpen(false);
            onMenuSelect?.();
        }
    };

    if (hideTrigger) {
        return (
            <>
                <Modal
                    title={`Select ${kindConfig.label}`}
                    open={openMediaModel}
                    onCancel={() => setOpenMediaModel(false)}
                    footer={null}
                    width={1200}
                    destroyOnClose
                >
                    <MediModel
                        HandleUploadImage={HandleUploadImage}
                        selectionMode="multi"
                        maxSelection={maxSelection}
                        selectedCount={selectedCount}
                        selectedMediaIds={selectedMediaIds}
                        mediaKindFilter={mediaKind}
                    />
                </Modal>
            </>
        );
    }

    return (
        <>
            <Modal
                title={`Select ${kindConfig.label}`}
                open={openMediaModel}
                onCancel={() => setOpenMediaModel(false)}
                footer={null}
                width={1200}
                destroyOnClose
            >
                <MediModel
                    HandleUploadImage={HandleUploadImage}
                    selectionMode="multi"
                    maxSelection={maxSelection}
                    selectedCount={selectedCount}
                    selectedMediaIds={selectedMediaIds}
                    mediaKindFilter={mediaKind}
                />
            </Modal>

            <Tooltip title={slotsLeft <= 0 ? `Maximum ${maxSelection} files` : ''}>
                <Dropdown
                    placement="topLeft"
                    menu={{ items: menuItems, onClick: onMenuClick }}
                    trigger={['click']}
                    disabled={disabled || isUploading}
                    open={dropdownOpen}
                    onOpenChange={setDropdownOpen}
                >
                    {variant === 'add' ? (
                        <div
                            style={{
                                flexShrink: 0,
                                width: 56,
                                height: 56,
                                borderRadius: 8,
                                border: '1px dashed #bfbfbf',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                                color: '#8c8c8c',
                                background: '#fafafa',
                            }}
                        >
                            <PlusOutlined style={{ fontSize: 22 }} />
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#1890ff',
                                cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <PaperClipOutlined style={{ fontSize: showLabel ? '18px' : '20px' }} />
                            {showLabel && <span style={{ color: '#000' }}>Upload</span>}
                        </div>
                    )}
                </Dropdown>
            </Tooltip>
        </>
    );
});

UploadDrawer.displayName = 'UploadDrawer';

export default UploadDrawer;
